import { ErrorModal } from "@dexteel/mesf-core";
import { Box, CircularProgress, Paper, Typography } from "@mui/material";
import moment from "moment";
import { useEffect, useState } from "react";
import { AccountSummaryGrid } from "@/pages/reports/summary-by-close-date/components/AccountSummaryGrid";
import { useCostCenters } from "@/pages/reports/summary-by-close-date/hooks/useSummaryByCloseDate";
import {
	isUsableDate,
	urlParam,
	useUrlState,
} from "@/shared/hooks/useUrlState";
import { useReportPdfExport } from "@/shared/hooks/useReportPdfExport";
import { getApiPdf } from "@/shared/repositories/ReportPdfRepository";
import { useAccountSummaryByDateRange } from "../hooks/useSummaryByDateRange";
import { DateRangeSummaryHeader } from "./DateRangeSummaryHeader";
import { SummaryByDateRangeToolbar } from "./SummaryByDateRangeToolbar";

const FILLER_SX = {
	flex: 1,
	minHeight: 0,
	display: "flex",
	alignItems: "center",
	justifyContent: "center",
} as const;

const URL_SCHEMA = {
	costCenterId: urlParam.int(),
	start: urlParam.date(() => moment().startOf("day")),
	end: urlParam.date(() => moment().startOf("day")),
};

export const SummaryByDateRangeReport = () => {
	const [
		{ costCenterId, start, end },
		setFilters,
		{ dropValues, resetValues, isDefault },
	] = useUrlState(URL_SCHEMA);
	const [errorMsg, setErrorMsg] = useState("");

	const costCentersQuery = useCostCenters();
	const summaryQuery = useAccountSummaryByDateRange(
		costCenterId,
		start.format("YYYY-MM-DD"),
		end.format("YYYY-MM-DD"),
	);

	// A shared link can name a cost center that is no longer active.
	useEffect(() => {
		if (!costCentersQuery.isSuccess || costCenterId == null) return;
		if (!costCentersQuery.data.some((cc) => cc.Id === costCenterId))
			dropValues("costCenterId");
	}, [
		costCentersQuery.isSuccess,
		costCentersQuery.data,
		costCenterId,
		dropValues,
	]);

	const error = costCentersQuery.error ?? summaryQuery.error;
	if (error && !errorMsg) setErrorMsg((error as Error).message);

	const summary = summaryQuery.data ?? null;

	const { exporting: exportingPdf, exportPdf } =
		useReportPdfExport(setErrorMsg);
	const handleExportPdf = () => {
		if (costCenterId == null || !summary) return;
		exportPdf(
			`Summary By Date Range ${summary.CostCenterName?.trim() ?? costCenterId} ${start.format("MM-DD-YYYY")} to ${end.format("MM-DD-YYYY")}.pdf`,
			() =>
				getApiPdf("api/account-summary/by-range/pdf", {
					costCenterId: String(costCenterId),
					start: start.format("YYYY-MM-DD"),
					close: end.format("YYYY-MM-DD"),
				}),
		);
	};

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				height: "calc(100vh - 64px)",
				gap: 1,
				p: 1,
				bgcolor: "#f0f2f5",
			}}
		>
			<Typography variant="h5" sx={{ fontWeight: 600 }}>
				Summary By Date Range
			</Typography>

			<Paper elevation={3}>
				<SummaryByDateRangeToolbar
					costCenters={costCentersQuery.data ?? []}
					costCenterId={costCenterId}
					onCostCenterChange={(value) => setFilters({ costCenterId: value })}
					start={start}
					end={end}
					onStartChange={(date) =>
						isUsableDate(date) && setFilters({ start: date })
					}
					onEndChange={(date) =>
						isUsableDate(date) && setFilters({ end: date })
					}
					onReset={() => resetValues()}
					canReset={!isDefault()}
					onExportPdf={handleExportPdf}
					canExportPdf={
						costCenterId != null && !summaryQuery.isFetching && !!summary
					}
					exportingPdf={exportingPdf}
				/>
			</Paper>

			{costCenterId == null && (
				<Paper elevation={3} sx={FILLER_SX}>
					<Typography variant="body1" color="text.secondary">
						Pick a cost center
					</Typography>
				</Paper>
			)}

			{costCenterId != null && summaryQuery.isInitialLoading && (
				<Paper elevation={3} sx={FILLER_SX}>
					<CircularProgress />
				</Paper>
			)}

			{costCenterId != null && !summaryQuery.isInitialLoading && !summary && (
				<Paper elevation={3} sx={FILLER_SX}>
					<Typography variant="body1" color="text.secondary">
						No summary for the selected cost center and date range
					</Typography>
				</Paper>
			)}

			{costCenterId != null && !summaryQuery.isInitialLoading && summary && (
				<AccountSummaryGrid
					summary={summary}
					header={<DateRangeSummaryHeader summary={summary} />}
				/>
			)}

			<ErrorModal error={errorMsg} onHide={() => setErrorMsg("")} />
		</Box>
	);
};
